import { ERentioFormField } from "@rentiohq/shared-frontend/dist/components/components/Form";
import { IUiSchema } from "@rentiohq/shared-frontend/dist/forms/forms.types";
import { IContactListDataSourceItem } from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import {
  contactSelectInlineV2ValueSelector,
  getValue,
} from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import { EContractMemberTypes } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { getMembersWithRole } from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import utils from "@rentiohq/web-shared/dist/utils";
import { JSONSchema6 } from "json-schema";
import { EField, IExtraData } from "./schema.registration.types";
import {
  getRegistrationCompanyFieldsSchema,
  getRegistrationContactFieldsSchema,
} from "./schema.registration.utils";

export const getSchema =
  (
    initialValues: {
      [key: string]: unknown;
    },
    extraData: IExtraData,
  ) =>
  (defaultValues: { [key: string]: unknown }): JSONSchema6 => {
    const { contactMeMaster, legalContact, isBroker } = extraData;

    return {
      required: [],
      properties: {
        title1: {
          default: undefined,
        },
        info1: {
          default: undefined,
        },
        [EField.Owners]: {
          default: getValue(EField.Owners, initialValues, defaultValues),
          // @ts-ignore
          requiredIf: (formData: any) =>
            (formData[EField.SignAsBroker] || []).length === 0,
        },
        [EField.SignAsBroker]: {
          default: getValue(EField.SignAsBroker, initialValues, defaultValues),
          // @ts-ignore
          showIf: () => isBroker && legalContact && contactMeMaster,
          // @ts-ignore
          requiredIf: (formData: any) =>
            (formData[EField.Owners] || []).length === 0,
          options: (() => {
            if (!contactMeMaster) {
              return [];
            }

            return [
              {
                id: contactMeMaster.account.id,
                value: getName(contactMeMaster.account),
                subValue: getLocalizedText("role.broker"),
              },
            ];
          })(),
        },
      },
    };
  };

export const uiSchema = (extraData: IExtraData): IUiSchema<EField> => {
  const { contract, contactMeMaster, isBroker } = extraData;

  return {
    "rentio:title": utils.i18n.t("registration.create.form.step.owners"),
    "rentio:navigationTitle": utils.i18n.t("registration.create.title"),
    title1: {
      "ui:field": ERentioFormField.SectionHeader,
      title: utils.i18n.t("registration.create.form.field.owners_title"),
    },
    info1: {
      "ui:field": ERentioFormField.HelpInfo,
      subtitle: getLocalizedText("registration.create.form.field.owners_info"),
    },
    [EField.Owners]: {
      "ui:field": ERentioFormField.ContactSelectInlineV2,
      title: getLocalizedText("registration.create.form.field.owners"),
      isMultiSelect: true,
      asOptionList: true,
      getRequiredContactFieldsSchema: getRegistrationContactFieldsSchema,
      getRequiredCompanyFieldsSchema: getRegistrationCompanyFieldsSchema,
      valueSelector: (
        state: IPartialRootState,
        selectedAccountIds: number[] | string = [],
      ) =>
        contactSelectInlineV2ValueSelector(
          state,
          selectedAccountIds,
          contract.members.map(member => member.account),
        ),
      customGetSubtitleSelector: (
        _: IPartialRootState,
        formData: any,
        item: IContactListDataSourceItem,
      ) => {
        const { accountId, contact } = item;

        const member = contract.members.find(member => {
          if (accountId && accountId === member.account.id) {
            return true;
          }

          if (contact?.accountIds.includes(member.account.id)) {
            return true;
          }

          return false;
        });

        if (!member) {
          return;
        }

        return member.roles
          .map(role => getLocalizedText(`role.${role.toLowerCase()}`))
          .join(", ");
      },
      customExtraAccountsSelector: () =>
        getMembersWithRole(contract.members, EContractMemberTypes.Owner).map(
          member => member.account,
        ),
      customIsFetchingSelector: () => false,
      customGetContactsActions: [],
      getDisallowedAccountIds: (_: any) =>
        contactMeMaster && isBroker ? [contactMeMaster.account.id] : [],
    },
    [EField.SignAsBroker]: {
      "ui:field": ERentioFormField.InlineSelect,
      title: getLocalizedText("registration.create.form.field.sign_as_broker"),
      isMultiSelect: true,
      asOptionList: true,
      hideOnSummaryIfEmpty: true,
      formatSummary: (formData: any) => {
        if (!formData || !contactMeMaster) {
          return;
        }

        return getName(contactMeMaster.account);
      },
    },
  };
};
